export const SET_OPTIMIZELY_FEATURE_USER_DATA = 'SET_OPTIMIZELY_FEATURE_USER_DATA'
export const SET_OPTIMIZELY_FEATURE_USER_ATTRIBUTE = 'SET_OPTIMIZELY_FEATURE_USER_ATTRIBUTE'
export const SET_OPTIMIZELY_FEATURE_DECISION = 'SET_OPTIMIZELY_FEATURE_DECISION'
export const SET_OPTIMIZELY_FEATURE_MOUNTED = 'SET_OPTIMIZELY_FEATURE_MOUNTED'
export const SET_OPTIMIZELY_FEATURE_FLAG = 'SET_OPTIMIZELY_FEATURE_FLAG'
export const UPDATE_OPTIMIZELY_FEATURE_FLAG = 'UPDATE_OPTIMIZELY_FEATURE_FLAG'

/**
 * Sets the Optimizely feature as mounted.
 * This action is dispatched when the Optimizely feature is mounted in the application.
 * It updates the state to reflect that the feature is ready for use.
 * @function setOptimizelyFeatureMounted
 * @param {Object} payload - The payload containing the mounted state.
 * @returns {Object} - The action object to be dispatched.
 */
export function setOptimizelyFeatureMounted (payload) {
  return {
    type: SET_OPTIMIZELY_FEATURE_MOUNTED,
    payload,
  }
}

/**
 * Set the Optimizely feature flag.
 * This action is dispatched to set a specific feature flag in the Optimizely feature state.
 * It updates the state with the provided flag.
 * @function setOptimizelyFeatureFlag
 * @param {string} flag - The feature flag to be set.
 * @returns {Object} - The action object to be dispatched.
 */
export function setOptimizelyFeatureFlag (flag) {
  return {
    type: SET_OPTIMIZELY_FEATURE_FLAG,
    payload: { flag },
  }
}

/**
 * Sets the Optimizely feature decision based on the provided decision object.
 * This action is dispatched when a decision is made regarding the feature flag,
 * such as whether a feature is enabled or which variation is selected.
 * @function setOptimizelyFeatureDecision
 * @param {Object} decision - The decision object containing details about the feature flag.
 * @param {string} decision.flagKey - The key of the feature flag.
 * @param {boolean} decision.enabled - Whether the feature is enabled.
 * @param {string} decision.variationKey - The key of the selected variation.
 * @param {Object} decision.variables - Any variables associated with the feature flag.
 * @param {string} decision.ruleKey - The key of the rule that determined the decision.
 * @returns {Object} - The action object to be dispatched.
 */
export function setOptimizelyFeatureDecision (decision) {
  const {
    flagKey: flag,
    enabled,
    variationKey: variation,
    variables,
    ruleKey: rule,
  } = decision

  const payload = {
    [flag]: {
      enabled,
      variation,
      variables,
      rule,
    },
  }

  return updateOptimizelyFeatureFlag(payload)
}

/**
 * Sets the Optimizely feature user data.
 * This action is dispatched to set the user data for the Optimizely feature
 * @function setOptimizelyFeatureUser
 * @param {Object} payload - The payload containing user data.
 * @returns {Object} - The action object to be dispatched.
 */
export function setOptimizelyFeatureUser (payload) {
  return {
    type: SET_OPTIMIZELY_FEATURE_USER_DATA,
    payload,
  }
}

/**
 * Updates the Optimizely feature user attributes.
 * This action is dispatched to update the user attributes for the Optimizely feature.
 * @function updateOptimizelyFeatureUser
 * @param {Object} payload - The payload containing user attributes.
 * @returns {Object} - The action object to be dispatched.
 */
export function updateOptimizelyFeatureUser (payload) {
  return {
    type: SET_OPTIMIZELY_FEATURE_USER_ATTRIBUTE,
    payload: { attributes: payload },
  }
}

/**
 * Sets in the flag decision data for the Optimizely feature.
 * @function updateOptimizelyFeatureFlag
 * @param {Object} payload - The payload containing the feature flag data.
 * @returns {Object} - The action object to be dispatched.
 */
export function updateOptimizelyFeatureFlag (payload) {
  return {
    type: UPDATE_OPTIMIZELY_FEATURE_FLAG,
    payload,
  }
}
